/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\WorldMap.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the geoFirewall module.
----------------------------------------------------------------*/


import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Globe } from 'lucide-react';
import { COUNTRIES } from '../../../data/countries';
import { useI18n } from '../../../hooks/useI18n';
import { buildWorldMapControls } from '../hooks/useWorldMapControls';
import { useWorldMapColors } from '../hooks/useWorldMapColors';
import { WorldMapBackground } from './WorldMapBackground';
import { WorldMapTooltip } from './WorldMapTooltip';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let jsVectorMap: any = null;

const SELECTED_COLOR = '#dc2626';
const DEFAULT_COLOR = '#22c55e';

const getCountryNameByCode = (code: string): string => {
    const upper = code.toUpperCase();
    return COUNTRIES.find((c) => c.code.toUpperCase() === upper)?.name ?? upper;
};

interface WorldMapProps {
    selectedCountries: string[];
    onCountryClick: (code: string, name: string) => void;
    onMapDataLoaded?: (countries: { code: string; name: string }[]) => void;
    initialSelectedCountries?: string[];
}

export interface WorldMapRef {
    focusCountry: (code: string) => void;
    zoomIn: () => void;
    zoomOut: () => void;
    reset: () => void;
}

export const WorldMap = forwardRef<WorldMapRef, WorldMapProps>((
    { selectedCountries, onCountryClick, onMapDataLoaded, initialSelectedCountries = [] }, ref
) => {
    const { t } = useI18n();
    const [mapLibLoaded, setMapLibLoaded] = useState(false);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<any>(null);
    const eventListenersRef = useRef<Map<SVGPathElement, () => void>>(new Map());
    const [tooltip, setTooltip] = useState({ name: '', x: 0, y: 0, visible: false });

    // -- Imperative handle -------------------------------------
    useImperativeHandle(ref, () => buildWorldMapControls(mapInstanceRef, mapContainerRef));

    // -- Load jsVectorMap library ------------------------------
    useEffect(() => {
        if (mapLibLoaded) return;
        (async () => {
            try {
                const module = await import('jsvectormap');
                jsVectorMap = module.default || module;
                if (typeof window !== 'undefined') (window as any).jsVectorMap = jsVectorMap;
                await import('jsvectormap/dist/maps/world.js');
                setMapLibLoaded(true);
            } catch (e) { console.error('Failed to load jsVectorMap', e); }
        })();
    }, [mapLibLoaded]);

    // -- SVG color updates -------------------------------------
    useWorldMapColors(selectedCountries, mapLibLoaded, mapInstanceRef, mapContainerRef, eventListenersRef, setTooltip);

    // -- Initialize map ----------------------------------------
    useEffect(() => {
        if (!mapLibLoaded || !mapContainerRef.current || mapInstanceRef.current) return;
        const container = mapContainerRef.current;
        if (container.clientWidth === 0) return;

        try {
            const map = new jsVectorMap({
                selector: container,
                map: 'world',
                zoomButtons: false, zoomOnScroll: true, bindTouchEvents: true,
                zoomMin: 1, zoomMax: 20, showTooltip: true,
                backgroundColor: 'transparent', fitToContainer: true,
                regionStyle: {
                    initial: { fill: DEFAULT_COLOR, fillOpacity: 1, stroke: '#6b7280', strokeWidth: 0.5, strokeOpacity: 1 },
                    hover: { fillOpacity: 0.8, cursor: 'pointer' },
                    selected: { fill: SELECTED_COLOR },
                    selectedHover: { fill: SELECTED_COLOR, fillOpacity: 0.8 },
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onRegionTooltipShow: (event: any, _: any, code: string) => {
                    let name = getCountryNameByCode(code);
                    if (name === code.toUpperCase()) {
                        const paths = map?.mapData?.paths || (window as any).jsVectorMap?.maps?.world?.paths;
                        if (paths) name = paths[code]?.name || paths[code.toUpperCase()]?.name || paths[code.toLowerCase()]?.name || name;
                    }
                    setTooltip((prev) => ({ ...prev, name, visible: true }));
                    try { event?.preventDefault(); } catch {} return false;
                },
                onRegionTooltipHide: () => setTooltip((prev) => ({ ...prev, visible: false })),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onRegionClick: (_: any, code: string) => {
                    const upper = code.toUpperCase();
                    onCountryClick(upper, getCountryNameByCode(upper));
                },
            });
            mapInstanceRef.current = map;

            // Apply initial colors
            const applyInitialColors = (attempts = 0) => {
                const c = mapContainerRef.current;
                if (!c) return;
                const set = new Set(initialSelectedCountries.filter(Boolean).map((x) => x.toLowerCase()));
                const paths = c.querySelectorAll<SVGPathElement>('path.jvm-region');
                if (paths.length === 0 && attempts < 5) { setTimeout(() => applyInitialColors(attempts + 1), 200 * (attempts + 1)); return; }
                paths.forEach((el) => {
                    const code = el.getAttribute('data-code');
                    if (!code) return;
                    const color = set.has(code.toLowerCase()) ? SELECTED_COLOR : DEFAULT_COLOR;
                    el.style.setProperty('fill', color, 'important');
                    el.setAttribute('fill', color);
                });
            };
            applyInitialColors();

            // Notify map data loaded
            if (onMapDataLoaded) {
                // @ts-ignore
                const paths: Record<string, { name?: string }> | null = map.mapData?.paths || (window as any).jsVectorMap?.maps?.world?.paths || null;
                if (paths) {
                    onMapDataLoaded(Object.keys(paths).map((k) => ({ code: k.toUpperCase(), name: paths[k].name || k.toUpperCase() })));
                } else {
                    setTimeout(() => {
                        const els = mapContainerRef.current?.querySelectorAll('path.jvm-region');
                        if (!els?.length) return;
                        const seen = new Set<string>();
                        const list: { code: string; name: string }[] = [];
                        els.forEach((el) => {
                            const code = el.getAttribute('data-code');
                            if (code) { const u = code.toUpperCase(); if (!seen.has(u)) { seen.add(u); list.push({ code: u, name: getCountryNameByCode(u) }); } }
                        });
                        if (list.length) onMapDataLoaded(list);
                    }, 500);
                }
            }

            setTimeout(() => map.updateSize(), 100);
            return () => { mapInstanceRef.current?.destroy(); mapInstanceRef.current = null; };
        } catch (e) { console.error('Failed to init map', e); }
    }, [mapLibLoaded, initialSelectedCountries]);

    // -- ResizeObserver ----------------------------------------
    useEffect(() => {
        if (!mapContainerRef.current || !mapInstanceRef.current) return;
        const obs = new ResizeObserver(() => mapInstanceRef.current?.updateSize());
        obs.observe(mapContainerRef.current);
        return () => obs.disconnect();
    }, [mapLibLoaded]);

    // -- Loading -----------------------------------------------
    if (!mapLibLoaded) {
        return (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-900/50 to-slate-800/50 rounded-xl">
                <div className="text-center text-muted-foreground">
                    <div className="relative">
                        <Globe className="h-16 w-16 mx-auto mb-4 animate-spin text-emerald-500/50" />
                        <div className="absolute inset-0 h-16 w-16 mx-auto blur-xl bg-emerald-500/20 animate-pulse" />
                    </div>
                    <p className="text-sm font-medium">{t('geoFirewall.map.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col relative overflow-hidden rounded-xl">
            <WorldMapBackground />
            <WorldMapTooltip {...tooltip} />
            <div className="w-full h-full relative z-10" onMouseMove={(e) => setTooltip((prev) => ({ ...prev, x: e.clientX, y: e.clientY }))} onMouseLeave={() => setTooltip((prev) => ({ ...prev, visible: false }))}>
                <div ref={mapContainerRef} className="w-full h-full" style={{ height: '100%' }} />
            </div>
        </div>
    );
});

WorldMap.displayName = 'WorldMap';
